import React from 'react';
import { colors, borderRadius, transitions, typography } from '../../styles/theme';

/**
 * Button Component - Reusable button with variants
 * @param {Object} props
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.variant - 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {React.ReactNode} props.leftIcon - Icon to display before text
 * @param {React.ReactNode} props.rightIcon - Icon to display after text
 * @param {boolean} props.isLoading - Show loading state
 * @param {boolean} props.isDisabled - Disable the button
 * @param {Function} props.onClick - Click handler
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 * @param {string} props.type - Button type attribute
 */
function Button({
  children,
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  isLoading = false,
  isDisabled = false,
  onClick,
  className = '',
  style = {},
  type = 'button',
}) {
  const sizeMap = {
    sm: {
      padding: '6px 12px',
      fontSize: typography.fontSize.sm,
      height: '32px',
    },
    md: {
      padding: '10px 20px',
      fontSize: typography.fontSize.sm,
      height: '40px',
    },
    lg: {
      padding: '14px 28px',
      fontSize: typography.fontSize.base,
      height: '48px',
    },
  };

  const variantStyles = {
    primary: {
      backgroundColor: colors.textPrimary,
      color: colors.textInverse,
      border: 'none',
      hoverBg: colors.textSecondary,
    },
    secondary: {
      backgroundColor: colors.surface,
      color: colors.textPrimary,
      border: `1px solid ${colors.border}`,
      hoverBg: colors.surfaceHover,
    },
    outline: {
      backgroundColor: 'transparent',
      color: colors.accent,
      border: `1px solid ${colors.accent}`,
      hoverBg: colors.accentLight,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: colors.textSecondary,
      border: 'none',
      hoverBg: colors.surfaceHover,
    },
    danger: {
      backgroundColor: colors.danger,
      color: colors.textInverse,
      border: 'none',
      hoverBg: '#B02A37',
    },
  };

  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderRadius: borderRadius.md,
    fontWeight: typography.fontWeight.medium,
    cursor: isDisabled || isLoading ? 'not-allowed' : 'pointer',
    opacity: isDisabled || isLoading ? 0.6 : 1,
    transition: `all ${transitions.fast}`,
    ...sizeMap[size],
    ...variantStyles[variant],
    ...style,
  };

  const handleMouseEnter = (e) => {
    if (!isDisabled && !isLoading) {
      e.currentTarget.style.backgroundColor = variantStyles[variant].hoverBg;
    }
  };

  const handleMouseLeave = (e) => {
    if (!isDisabled && !isLoading) {
      e.currentTarget.style.backgroundColor = variantStyles[variant].backgroundColor;
    }
  };

  return (
    <button
      type={type}
      className={`btn btn-${variant} btn-${size} ${className}`}
      style={baseStyles}
      onClick={onClick}
      disabled={isDisabled || isLoading}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isLoading && (
        <span className="btn-spinner" style={{ marginRight: '8px' }}>
          ⟳
        </span>
      )}
      {!isLoading && leftIcon && (
        <span className="btn-icon-left">{leftIcon}</span>
      )}
      <span>{children}</span>
      {!isLoading && rightIcon && (
        <span className="btn-icon-right">{rightIcon}</span>
      )}
    </button>
  );
}

export default Button;
